<section class="reports-page">
    <header class="reports-header">
        <div>
            <p class="eyebrow">Shine AI reporting</p>
            <h1>Dashboard</h1>
            <p class="header-copy">Operational usage, reliability, and latency across chatbots, assistants, tasks, and workflows.</p>
        </div>

        <div class="report-controls" aria-label="Report filters">
            <mat-button-toggle-group
                [ngModel]="selectedRange"
                (ngModelChange)="applyQuickRange($event)"
                aria-label="Date range">
                @for (range of ranges; track range.value) {
                    <mat-button-toggle [value]="range.value">{{ range.label }}</mat-button-toggle>
                }
            </mat-button-toggle-group>

            <div class="date-controls">
                <mat-form-field appearance="outline">
                    <mat-label>Start</mat-label>
                    <input matInput type="date" [(ngModel)]="startDate" (ngModelChange)="selectedRange = 'custom'">
                </mat-form-field>

                <mat-form-field appearance="outline">
                    <mat-label>End</mat-label>
                    <input matInput type="date" [(ngModel)]="endDate" (ngModelChange)="selectedRange = 'custom'">
                </mat-form-field>

                <button mat-flat-button color="primary" type="button" (click)="applyDateRange()" [disabled]="!startDate || !endDate || loading">
                    Apply
                </button>
            </div>
        </div>
    </header>

    @if (loadError) {
        <div class="report-alert" role="status">{{ loadError }}</div>
    }

    @if (loading) {
        <div class="loading-state" role="status">
            <mat-spinner diameter="40"></mat-spinner>
            <span>Loading reports</span>
        </div>
    } @else {
        @if (conversationReport) {
            <section class="executive-report">
                <div class="executive-heading">
                    <div>
                        <p class="eyebrow">Reporting view</p>
                        <h2>Client conversation performance</h2>
                        <p>Conversations, minutes, messages, and feedback from the persisted conversation source.</p>
                    </div>

                    <mat-button-toggle-group
                        [ngModel]="selectedGrain"
                        (ngModelChange)="applyReportGrain($event)"
                        aria-label="Report grouping">
                        @for (grain of grains; track grain.value) {
                            <mat-button-toggle [value]="grain.value">{{ grain.label }}</mat-button-toggle>
                        }
                    </mat-button-toggle-group>
                </div>

                <div class="kpi-grid executive-kpis">
                    @for (kpi of conversationKpis; track trackKpi($index, kpi)) {
                        <article class="kpi-tile" [class]="'tone-' + kpi.tone">
                            <span>{{ kpi.label }}</span>
                            <strong>{{ kpi.value }}</strong>
                            <small>{{ kpi.hint }}</small>
                        </article>
                    }
                </div>

                <div class="executive-grid">
                    <article class="chart-panel trend-panel">
                        <header>
                            <h3>Conversation trend</h3>
                            <span>{{ conversationReport.startDate }} to {{ conversationReport.endDate }}</span>
                        </header>
                        <apx-chart
                            [series]="conversationTrendSeries"
                            [chart]="conversationChart"
                            [xaxis]="conversationXAxis"
                            [yaxis]="conversationYAxis"
                            [stroke]="conversationStroke"
                            [dataLabels]="conversationDataLabels"
                            [legend]="conversationLegend"
                            [tooltip]="conversationTooltip"
                            [grid]="conversationGrid">
                        </apx-chart>
                    </article>

                    <article class="feedback-panel">
                        <header>
                            <h3>Feedback quality</h3>
                            <span>{{ conversationReport.feedbackTotal }} votes</span>
                        </header>
                        <div class="gauge" [style.background]="feedbackGaugeStyle">
                            <div class="gauge-inner">
                                <strong>{{ conversationReport.positiveFeedbackRate }}%</strong>
                                <span>thumbs up</span>
                            </div>
                        </div>
                        <div class="feedback-split">
                            <span><strong>{{ conversationReport.thumbsUp }}</strong> positive</span>
                            <span><strong>{{ conversationReport.thumbsDown }}</strong> negative</span>
                        </div>
                    </article>

                    @if (conversationReport.categories.length) {
                        <article class="chart-panel">
                            <header>
                                <h3>Conversation categories</h3>
                                <span>{{ conversationReport.categories.length }} categories</span>
                            </header>
                            <apx-chart
                                [series]="conversationCategorySeries"
                                [chart]="conversationCategoryChart"
                                [xaxis]="conversationCategoryXAxis"
                                [yaxis]="conversationYAxis"
                                [dataLabels]="conversationDataLabels"
                                [tooltip]="conversationTooltip"
                                [grid]="conversationGrid">
                            </apx-chart>
                        </article>
                    }
                </div>

                <div class="report-table-wrap">
                    <table class="report-table executive-table">
                        <thead>
                        <tr>
                            <th scope="col">Period</th>
                            <th scope="col" class="align-right">Conversations</th>
                            <th scope="col" class="align-right">Minutes</th>
                            <th scope="col" class="align-right">Messages</th>
                            <th scope="col" class="align-right">Thumbs up</th>
                            <th scope="col" class="align-right">Feedback</th>
                        </tr>
                        </thead>
                        <tbody>
                            @for (period of conversationReport.periods; track period.periodStart) {
                                <tr>
                                    <td>
                                        <strong>{{ period.periodLabel }}</strong>
                                        <small>{{ period.periodStart }}</small>
                                    </td>
                                    <td class="align-right">{{ period.conversations }}</td>
                                    <td class="align-right">{{ period.conversationMinutes | number:'1.0-1' }}</td>
                                    <td class="align-right">{{ period.messages }}</td>
                                    <td class="align-right">{{ period.thumbsUp }}</td>
                                    <td class="align-right">
                                        <span class="health-pill" [class]="period.positiveFeedbackRate >= 85 ? 'tone-good' : period.positiveFeedbackRate >= 65 ? 'tone-warn' : period.feedbackTotal > 0 ? 'tone-bad' : 'tone-neutral'">
                                            {{ period.positiveFeedbackRate }}%
                                        </span>
                                    </td>
                                </tr>
                            }
                        </tbody>
                    </table>
                </div>
            </section>
        }

        @if (conversationReport?.functionCallReportingStatus === 'unavailable') {
            <div class="report-alert" role="status">Bot-generated events are temporarily unavailable. Conversation metrics are still shown. Try again or narrow the date range (up to 92 days and 20,000 stored calls).</div>
        }
        @if (eventReport; as calls) {
            <section class="executive-report bot-events-report" aria-labelledby="bot-events-heading">
                <div class="executive-heading">
                    <div>
                        <p class="eyebrow">Bot activity</p>
                        <h2 id="bot-events-heading">Bot-generated events</h2>
                        <p>Recorded function calls in the selected date range. Includes lookups and actions; successful bookings or enquiries are not confirmed by this count.</p>
                    </div>
                    @if (conversationReport?.functionCalls?.chatbots?.length) {
                        <mat-form-field appearance="outline">
                            <mat-label>Bot events for chatbot</mat-label>
                            <mat-select [(ngModel)]="selectedEventChatbot" aria-label="Bot events for chatbot">
                                <mat-option value="all">All chatbots</mat-option>
                                @for (bot of conversationReport?.functionCalls?.chatbots; track bot.chatbotId) {
                                    <mat-option [value]="eventChatbotKey(bot.chatbotId)">{{ eventChatbotLabel(bot.chatbotId) }}</mat-option>
                                }
                            </mat-select>
                            <mat-hint>Filters only bot-generated events</mat-hint>
                        </mat-form-field>
                    }
                </div>
                <div class="kpi-grid">
                    <article class="kpi-tile tone-neutral">
                        <span>Recorded calls</span>
                        <strong>{{ calls.totalCalls | number }}</strong>
                        <small>Counted by call time (UTC)</small>
                    </article>
                    <article class="kpi-tile tone-neutral">
                        <span>Conversations with calls</span>
                        <strong>{{ calls.conversationsWithCalls | number }}</strong>
                        <small>Distinct conversations with recorded calls</small>
                    </article>
                    @if (calls.conversationsWithActions !== undefined) {
                        <article class="kpi-tile tone-neutral">
                            <span>Conversations with actions</span>
                            <strong>{{ calls.conversationsWithActions | number }}</strong>
                            <small>Email, workflow or configured external calls</small>
                        </article>
                    }
                    @if (calls.outcomeCoveragePercent !== undefined) {
                        <article class="kpi-tile tone-neutral">
                            <span>Known outcome coverage</span>
                            <strong>{{ calls.totalCalls ? calls.outcomeCoveragePercent + '%' : 'No data' }}</strong>
                            <small>{{ calls.callsWithKnownOutcome | number }} of {{ calls.totalCalls | number }} calls have a recorded status</small>
                        </article>
                    }
                </div>
                <p class="bot-events-note">Types describe technical calls: workflow/external calls may also perform lookups. Limited to 92 days and 20,000 stored calls. Status describes technical execution. Unknown includes older or unrecognised status values. Retention and missing timestamps may limit historical coverage. Conversation counts overlap between groups and must not be added together.</p>
                @if (calls.totalCalls) {
                    @if (calls.types && calls.outcomes) {
                        <div class="bot-events-grid">
                            @for (dimension of [{title: 'Calls by type', rows: calls.types}, {title: 'Calls by status', rows: calls.outcomes}]; track dimension.title) {
                                <div class="report-table-wrap">
                                    <table class="report-table">
                                        <caption>{{ dimension.title }}</caption>
                                        <thead><tr><th scope="col">Group</th><th scope="col" class="align-right">Calls</th><th scope="col" class="align-right">Conversations</th></tr></thead>
                                        <tbody>
                                            @for (row of dimension.rows; track row.key) {
                                                <tr><th scope="row">{{ eventDimensionLabel(row.key) }}</th><td class="align-right">{{ row.calls | number }}</td><td class="align-right">{{ row.conversations | number }}</td></tr>
                                            }
                                        </tbody>
                                    </table>
                                </div>
                            }
                        </div>
                    }
                    <div class="bot-events-grid">
                        <div class="report-table-wrap">
                            <table class="report-table">
                                <caption>Calls by function</caption>
                                <thead><tr>
                                    <th scope="col">Function</th>
                                    <th scope="col" class="align-right">Calls</th>
                                    <th scope="col" class="align-right">Conversations</th>
                                </tr></thead>
                                <tbody>
                                    @for (fn of calls.functions; track fn.functionName) {
                                        <tr>
                                            <th scope="row">{{ fn.functionName }}
                                                @for (outcome of fn.outcomes; track outcome.key) {
                                                    <small>{{ eventDimensionLabel(outcome.key) }}: {{ outcome.calls | number }}</small>
                                                }
                                            </th>
                                            <td class="align-right">{{ fn.calls | number }}</td>
                                            <td class="align-right">{{ fn.conversations | number }}</td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                        <div class="report-table-wrap">
                            <table class="report-table">
                                <caption>Calls by period</caption>
                                <thead><tr>
                                    <th scope="col">Period</th>
                                    <th scope="col" class="align-right">Calls</th>
                                    <th scope="col" class="align-right">Conversations</th>
                                </tr></thead>
                                <tbody>
                                    @for (period of calls.periods; track period.periodStart) {
                                        <tr>
                                            <th scope="row">{{ period.periodLabel }}<small>{{ period.periodStart }}</small></th>
                                            <td class="align-right">{{ period.calls | number }}</td>
                                            <td class="align-right">{{ period.conversations | number }}</td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>
                } @else {
                    <div class="empty-state" role="status">
                        <strong>No recorded function calls in this date range</strong>
                        <span>Choose another date range to check earlier activity.</span>
                    </div>
                }
            </section>
        }

        <mat-tab-group animationDuration="150ms" mat-stretch-tabs="false" mat-align-tabs="start">
            @for (section of sections; track trackSection($index, section)) {
                <mat-tab [label]="section.title">
                    <section class="report-section">
                        <div class="section-heading">
                            <div>
                                <h2>{{ section.title }}</h2>
                                <p>{{ section.subtitle }}</p>
                            </div>
                            @if (section.error) {
                                <span class="section-error">{{ section.error }}</span>
                            }
                        </div>

                        <div class="kpi-grid">
                            @for (kpi of section.kpis; track trackKpi($index, kpi)) {
                                <article class="kpi-tile" [class]="'tone-' + kpi.tone">
                                    <span>{{ kpi.label }}</span>
                                    <strong>{{ kpi.value }}</strong>
                                    <small>{{ kpi.hint }}</small>
                                </article>
                            }
                        </div>

                        <div class="chart-grid">
                            <article class="chart-panel">
                                <header>
                                    <h3>{{ section.timelineTitle }}</h3>
                                    <span>{{ startDate }} to {{ endDate }}</span>
                                </header>
                                <apx-chart
                                    [series]="section.timelineSeries"
                                    [chart]="section.chart"
                                    [xaxis]="section.xAxis"
                                    [yaxis]="section.yAxis"
                                    [stroke]="section.stroke"
                                    [dataLabels]="section.dataLabels"
                                    [tooltip]="section.tooltip"
                                    [grid]="section.grid">
                                </apx-chart>
                            </article>

                            <article class="chart-panel">
                                <header>
                                    <h3>{{ section.volumeTitle }}</h3>
                                    <span>{{ section.rows.length }} active {{ section.title.toLowerCase() }}</span>
                                </header>
                                <apx-chart
                                    [series]="section.volumeSeries"
                                    [chart]="section.barChart"
                                    [xaxis]="section.xAxis"
                                    [yaxis]="section.yAxis"
                                    [dataLabels]="section.dataLabels"
                                    [legend]="section.legend"
                                    [tooltip]="section.tooltip"
                                    [grid]="section.grid">
                                </apx-chart>
                            </article>
                        </div>

                        <div class="table-toolbar">
                            <mat-form-field appearance="outline">
                                <mat-label>{{ section.searchPlaceholder }}</mat-label>
                                <input
                                    matInput
                                    [ngModel]="section.filterText"
                                    (ngModelChange)="applySectionFilter(section, $event)"
                                    type="search">
                            </mat-form-field>

                            <div class="table-meta">
                                <span>{{ section.filteredRows.length }} of {{ section.rows.length }}</span>
                                @if (section.filterText) {
                                    <button mat-button type="button" (click)="clearSectionFilter(section)">Clear</button>
                                }
                            </div>
                        </div>

                        <div class="report-table-wrap">
                            @if (section.filteredRows.length) {
                                <table class="report-table">
                                    <thead>
                                    <tr>
                                        <th scope="col">Name</th>
                                        @for (column of section.columns; track trackColumn($index, column)) {
                                            <th scope="col" [class.align-right]="column.align === 'right'">{{ column.label }}</th>
                                        }
                                    </tr>
                                    </thead>
                                    <tbody>
                                        @for (row of section.filteredRows; track trackRow($index, row)) {
                                            <tr>
                                                <td>
                                                    <a [routerLink]="row.route" class="entity-link">
                                                        <img [src]="row.image" alt="" width="40" height="40">
                                                        <span>
                                                            <strong>{{ row.name }}</strong>
                                                            <small>{{ row.id }}</small>
                                                        </span>
                                                    </a>
                                                </td>
                                                @for (column of section.columns; track trackColumn($index, column)) {
                                                    <td [class.align-right]="column.align === 'right'">
                                                        @if (column.key === 'health') {
                                                            <span class="health-pill" [class]="'tone-' + row.healthTone">
                                                                {{ row.values[column.key] }}
                                                            </span>
                                                        } @else {
                                                            {{ row.values[column.key] }}
                                                        }
                                                    </td>
                                                }
                                            </tr>
                                        }
                                    </tbody>
                                </table>
                            } @else {
                                <div class="empty-state">
                                    <strong>No matching report rows</strong>
                                    <span>Adjust the search or date range.</span>
                                </div>
                            }
                        </div>
                    </section>
                </mat-tab>
            }
        </mat-tab-group>
    }
</section>
